import React, { useState } from "react";
import { generateLeads, importLeads } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Search, Bot, Plus, Check, Globe, Loader2 } from "lucide-react";
import { RadiusControls, useVillesAround } from "./autopilot/VillePicker";

const LeadsPanel = () => {
  const [secteur, setSecteur] = useState("");
  const [ville, setVille] = useState("Hauts-de-France");
  const [count, setCount] = useState(8);
  const [radius, setRadius] = useState(40);
  const [minPop, setMinPop] = useState(5000);
  const { villes } = useVillesAround(radius, minPop);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState({});
  const { toast } = useToast();

  const search = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLeads([]);
    setSelected({});
    try {
      const res = await generateLeads({ secteur, ville, count: Number(count) });
      setLeads(res.data.leads || []);
      const sel = {};
      (res.data.leads || []).forEach((_, i) => (sel[i] = true));
      setSelected(sel);
      if ((res.data.leads || []).length === 0) {
        toast({ title: "Aucune entreprise avec contact vérifié", description: "Seules les entreprises avec un email ou un téléphone trouvés sur le web sont proposées. Élargissez la zone ou le secteur." });
      }
    } catch (err) {
      toast({ title: "Erreur", description: "Génération impossible", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const toggle = (i) => setSelected((s) => ({ ...s, [i]: !s[i] }));

  const importSelected = async () => {
    const chosen = leads.filter((_, i) => selected[i]);
    if (chosen.length === 0) return;
    try {
      const res = await importLeads({ leads: chosen, source: "IA web" });
      toast({ title: `${res.data.count} prospects ajoutés ✓`, description: "Retrouvez-les dans l'onglet Prospects." });
      setLeads([]);
      setSelected({});
    } catch (err) {
      toast({ title: "Erreur", description: "Import impossible", variant: "destructive" });
    }
  };

  const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand";
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Globe size={20} className="text-brand" />
        <h2 className="font-heading font-bold text-xl text-neutral-900">Recherche de sociétés (IA)</h2>
      </div>
      <p className="text-sm text-neutral-500 mb-5">
        L'agent IA propose des entreprises cibles à prospecter par secteur et zone. ⚠️ Suggestions à vérifier avant contact.
      </p>

      <form onSubmit={search} className="bg-white border border-neutral-200 rounded-xl p-5 grid sm:grid-cols-4 gap-3 items-end">
        <div className="sm:col-span-1">
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Secteur</label>
          <input className={inputCls} placeholder="BTP, industrie, tertiaire..." value={secteur} onChange={(e) => setSecteur(e.target.value)} />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Zone</label>
          <input className={inputCls} list="leads-villes" placeholder="Ville / département" value={ville} onChange={(e) => setVille(e.target.value)} data-testid="leads-ville-input" />
          <datalist id="leads-villes">{villes.map((v) => <option key={v.code} value={v.nom}>{`${v.distance_km} km`}</option>)}</datalist>
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Nombre</label>
          <input type="number" min={3} max={15} className={inputCls} value={count} onChange={(e) => setCount(e.target.value)} />
        </div>
        <button type="submit" disabled={loading} className="flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold py-2.5 rounded-md disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          {loading ? "Recherche..." : "Rechercher"}
        </button>
        <div className="sm:col-span-4 border-t border-neutral-100 pt-3">
          <RadiusControls radius={radius} setRadius={setRadius} minPop={minPop} setMinPop={setMinPop} idPrefix="leads" />
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto" data-testid="leads-villes-list">
            {villes.map((v) => (
              <button type="button" key={v.code} onClick={() => setVille(v.nom)} data-testid={`leads-ville-chip-${v.code}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${ville === v.nom ? "bg-brand text-white border-brand" : "bg-white border-neutral-300 text-neutral-700 hover:border-brand"}`}>
                {v.nom} <span className={ville === v.nom ? "opacity-80" : "text-neutral-400"}>· {v.distance_km} km</span>
              </button>
            ))}
          </div>
        </div>
        <p className="sm:col-span-4 text-xs text-neutral-500">Chaque entreprise proposée a obligatoirement un email et/ou un téléphone trouvés sur le web (site officiel, annuaires) — rien n'est inventé.</p>
      </form>

      {leads.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-neutral-600">{selectedCount} sélectionné(s) sur {leads.length}</p>
            <button onClick={importSelected} disabled={selectedCount === 0} className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-50">
              <Plus size={16} /> Ajouter au CRM
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {leads.map((l, i) => (
              <div
                key={`${l.societe}-${l.ville}-${i}`}
                onClick={() => toggle(i)}
                className={`cursor-pointer bg-white border rounded-xl p-4 transition-all ${
                  selected[i] ? "border-brand ring-2 ring-brand/20" : "border-neutral-200"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-neutral-900">{l.societe}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{l.secteur} · {l.ville}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${selected[i] ? "bg-brand text-white" : "border border-neutral-300"}`}>
                    {selected[i] && <Check size={13} />}
                  </div>
                </div>
                <p className="text-xs text-neutral-700 mt-1.5 flex flex-wrap gap-x-3" data-testid={`lead-contact-${i}`}>
                  {l.email && <span>✉ {l.email}</span>}
                  {l.telephone && <span>☎ {l.telephone}</span>}
                  {l.adresse && <span className="text-neutral-500">{l.adresse}</span>}
                </p>
                {l.notes && <p className="text-sm text-neutral-600 mt-2">{l.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && leads.length === 0 && (
        <div className="mt-10 text-center text-neutral-400">
          <Bot size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Lancez une recherche pour obtenir des cibles de prospection.</p>
        </div>
      )}
    </div>
  );
};

export default LeadsPanel;
